import { Component, computed, inject, input, LOCALE_ID, output, signal } from '@angular/core';
import { DatePipe } from '@angular/common';

import { PaymentRequestItem } from './payment-requests-table.module';
import { ActionInterface } from '../../../../../pages/admin-dashboard/pharmacies/pharmacies-list/pharmacies-list.model';
import { Loading } from '../../../../../utils/loading/loading';
import { ErrorData } from '../../../../../utils/error-data/error-data';
import { Tables } from '../../../../../shared/ui/shared-tables/tables/tables';
import { Pagination } from '../../../../../shared/ui/shared-tables/pagination/pagination';
import { AuthService } from '../../../../../services/auth-service';
import { SCOPES } from '../../../../../services/scope/scope-constants';

export type LoadState = 'loading' | 'success' | 'error';

const CANCEL_ACTION_ID = 6;

function parseCreatedOn(value: string): Date | null {
  if (!value) return null;

  const isoDate = new Date(value);
  if (!isNaN(isoDate.getTime())) return isoDate;

  const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(AM|PM)$/i);
  if (!match) return null;

  const [, day, month, year, hourStr, minute, meridiem] = match;
  let hour = parseInt(hourStr, 10);
  if (meridiem.toUpperCase() === 'PM' && hour !== 12) hour += 12;
  if (meridiem.toUpperCase() === 'AM' && hour === 12) hour = 0;

  return new Date(Number(year), Number(month) - 1, Number(day), hour, Number(minute));
}

function getPaymentMethodInfo(methodEn: string, displayLabel: string, isArabic: boolean) {
  const m = String(methodEn || '')
    .toLowerCase()
    .trim();
  const label = displayLabel || (isArabic ? 'غير محدد' : 'Unknown');

  if (m.includes('instapay') || m.includes('insta')) {
    return {
      label,
      svgType: 'instapay',
      colorClass: 'text-purple-600 bg-purple-50 border border-purple-200',
    };
  }

  if (m.includes('cash')) {
    return {
      label,
      svgType: 'cash',
      colorClass: 'text-emerald-600 bg-emerald-50 border border-emerald-200',
    };
  }

  if (m.includes('transfer')) {
    return {
      label,
      svgType: 'transfer',
      colorClass: 'text-indigo-600 bg-indigo-50 border border-indigo-200',
    };
  }

  if (m.includes('cheque') || m.includes('check')) {
    return {
      label,
      svgType: 'cheque',
      colorClass: 'text-amber-600 bg-amber-50 border border-amber-200',
    };
  }

  if (m.includes('wallet')) {
    return {
      label: isArabic ? 'محفظة إلكترونية' : 'E-Wallet',
      icon: 'solar:smartphone-line-duotone',
      colorClass: 'text-blue-600 bg-blue-50 border border-blue-200',
    };
  }

  return {
    label,
    icon: 'solar:card-transfer-bold-duotone',
    colorClass: 'text-gray-600 bg-gray-100 border border-gray-200',
  };
}

export function toPaymentRequestTableRows(items: PaymentRequestItem[], isArabic: boolean): Record<string, any>[] {
  return items.map((item) => {
    const rawMethod = isArabic ? item.paymentMethodAr : item.paymentMethodEn;

    return {
      id: item.id,
      amount: item.amount,
      pharmacyOwnerName: {
        pharmacyName: isArabic ? item.pharmacyNameAr : item.pharmacyNameEn,
        ownerName: item.ownerName,
      },
      status: {
        titleAr: item.statusAr,
        titleEn: item.statusEn,
      },
      paymentMethod: rawMethod,
      paymentMethodEn: item.paymentMethodEn,
      paymentMethodInfo: getPaymentMethodInfo(item.paymentMethodEn, rawMethod, isArabic),
      createdOnBy: {
        createdOn: parseCreatedOn(item.createdOn),
        createdBy: item.createdBy,
      },
      createdOn: parseCreatedOn(item.createdOn),
      createdBy: item.createdBy,
      isEditable: true,
    };
  });
}

@Component({
  selector: 'app-payment-requests-table',
  standalone: true,
  imports: [DatePipe, Loading, ErrorData, Tables, Pagination],
  templateUrl: './payment-requests-table.html',
  styleUrl: './payment-requests-table.scss',
})
export class PaymentRequestsTable {
  private locale = inject(LOCALE_ID);
  private authService = inject(AuthService);

  items = input<PaymentRequestItem[] | null>(null);
  state = input<LoadState>('loading');
  currentPage = input<number>(1);
  itemsPerPage = input<number>(25);
  totalItems = input<number>(0);
  totalPages = input<number>(1);
  isFiltersApplied = input<boolean>(false);

  pageChange = output<number>();
  pageSizeChange = output<number>();
  rowClick = output<string | number>();
  actionTrigger = output<{ event: Event; actionId: number; entity: any }>();

  rows = computed(() => toPaymentRequestTableRows(this.items() ?? [], this.isArabic()));

  activeDropdownId = signal<string | number | null>(null);
  currentActions = signal<ActionInterface[]>([]);

  tableHeaderData = signal([
    { key: 'id', label: $localize`:@@paymentRequestTableId:#`, type: 'idHash' },
    { key: 'amount', label: $localize`:@@paymentRequestTableAmount:المبلغ`, type: 'currency' },
    { key: 'pharmacyOwnerName', label: $localize`:@@paymentRequestTablePharmacyOwner:الصيدلية / المالك` },
    { key: 'status', label: $localize`:@@paymentRequestTableStatus:الحالة`, type: 'status' },
    { key: 'paymentMethod', label: $localize`:@@paymentRequestTablePaymentMethod:طريقة الدفع` },
    { key: 'createdOnBy', label: $localize`:@@paymentRequestTableCreatedInfo:الإنشاء في / بواسطة` },
    { key: 'actions', label: '', type: 'actions' },
  ]);

  startItem = computed(() => {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.itemsPerPage() + 1;
  });

  endItem = computed(() => {
    const end = this.currentPage() * this.itemsPerPage();
    return end > this.totalItems() ? this.totalItems() : end;
  });

  pageNumbers = computed(() => {
    const total = this.totalPages();
    const current = this.currentPage();
    const delta = 2;
    const range: number[] = [1];
    if (total <= 1) return range;
    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < total) range.push(i);
    }
    range.push(total);
    const result: number[] = [];
    let prev: number | undefined;
    for (const i of range) {
      if (prev !== undefined) {
        if (i - prev === 2) result.push(prev + 1);
        else if (i - prev !== 1) result.push(-1);
      }
      result.push(i);
      prev = i;
    }
    return result;
  });

  isArabic() {
    return this.locale.startsWith('ar');
  }

  canRequestModification = computed(() => {
    return this.authService.hasScopeById(SCOPES.PAYMENT_REQUESTS.REQUEST_MODIFICATION);
  });

  canSubmitModification = computed(() => {
    return this.authService.hasScopeById(SCOPES.PAYMENT_REQUESTS.SUBMIT_MODIFICATION);
  });

  canCancelRequest = computed(() => {
    return this.authService.hasScopeById(SCOPES.PAYMENT_REQUESTS.CANCEL);
  });

  private isCancelableStatus(statusEn: string): boolean {
    const s = String(statusEn || '')
      .toUpperCase()
      .trim();
    return s === 'PENDING' || s.includes('REVIEW') || s.includes('MODIF');
  }

  getActions(item: PaymentRequestItem): ActionInterface[] {
    const actions: ActionInterface[] = [{ id: 1, titleEn: 'View Details', titleAr: 'عرض التفاصيل' }];

    const normalizedStatus = String(item.statusEn || '')
      .toUpperCase()
      .trim()
      .replace(/\s+/g, '_');

    const isCancelled = normalizedStatus.includes('CANCEL');
    const isApproved = normalizedStatus.includes('APPROV');
    const isRejected = normalizedStatus.includes('REJECT');
    const isModificationRequested = normalizedStatus.includes('MODIF');
    const isPendingReview = normalizedStatus === 'PENDING' || normalizedStatus === 'PENDING_REVIEW';

    if (isCancelled || isApproved || isRejected) {
      return actions;
    }

    if (!isModificationRequested) {
      actions.push({ id: 2, titleEn: 'Accept', titleAr: 'قبول' }, { id: 3, titleEn: 'Reject', titleAr: 'رفض' });
    }

    if (this.canRequestModification() && isPendingReview) {
      actions.push({
        id: 4,
        titleEn: 'Request Modification',
        titleAr: 'طلب تعديل',
      });
    }

    if (isModificationRequested && this.canSubmitModification()) {
      actions.push({
        id: 5,
        titleEn: 'Edit',
        titleAr: 'تعديل',
      });
    }

    if (this.canCancelRequest() && this.isCancelableStatus(item.statusEn)) {
      actions.push({
        id: CANCEL_ACTION_ID,
        titleEn: 'Cancel Request',
        titleAr: 'إلغاء الطلب',
      });
    }

    return actions;
  }

  toggleDropdown(id?: any) {
    if (this.activeDropdownId() === id) {
      this.activeDropdownId.set(null);
    } else {
      this.activeDropdownId.set(id);
      const item = this.items()?.find((r) => r.id === id);
      if (item) this.currentActions.set(this.getActions(item));
    }
  }

  handleAction(e: { event: Event; actionId: number; entity: any }) {
    const { event } = e;
    event?.stopPropagation();
    this.actionTrigger.emit(e);
  }

  onPreviousPage() {
    this.pageChange.emit(this.currentPage() - 1);
  }

  onNextPage() {
    this.pageChange.emit(this.currentPage() + 1);
  }

  onGoToPage(page: number) {
    this.pageChange.emit(page);
  }

  onChangePageNumber(size: number) {
    this.pageSizeChange.emit(Number(size));
  }

  onPageSizeChange() {}
}
